import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Tabs, Table, Button, Modal, Form, Input, Select, DatePicker, Space, Tag,
  Typography, message, Popconfirm, List
} from 'antd';
import { NotificationOutlined, PlusOutlined, EyeOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const DEITIES = [
  { value: 'krishna', label: 'Krishna ji' },
  { value: 'ganesh', label: 'Ganpati ji' },
  { value: 'shiv', label: 'Shiv ji' },
  { value: 'durga', label: 'Maa Durga' },
  { value: 'laxmi', label: 'Laxmi ji / Diya' },
  { value: 'hanuman', label: 'Hanuman ji' },
  { value: 'ram', label: 'Shri Ram' },
  { value: 'saraswati', label: 'Maa Saraswati' },
  { value: 'surya', label: 'Surya Dev' },
  { value: 'rakhi', label: 'Rakhi (bhai-behen)' },
  { value: 'holi', label: 'Holi (rang)' },
  { value: 'flag', label: 'Tiranga' },
  { value: 'shubh', label: 'Shubh (diya)' },
];

export default function NoticeBoard() {
  const [notices, setNotices] = useState([]);
  const [staffTotal, setStaffTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [modal, setModal] = useState(false);
  const [festModal, setFestModal] = useState(false);
  const [readers, setReaders] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [festForm] = Form.useForm();

  const load = useCallback(() => {
    api.get('/notices').then(r => {
      setNotices(r.data.notices || []);
      setStaffTotal(r.data.staff_total || 0);
    }).catch(() => message.error('Notices load nahi hue'));
    api.get('/companies').then(r => setCompanies(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/festivals').then(r => setFestivals(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (values) => {
    setSaving(true);
    try {
      await api.post('/notices', {
        title: values.title,
        body: values.body,
        link: values.link,
        importance: values.importance || 'normal',
        company_id: values.company_id || null,
        expires_on: values.expires_on ? values.expires_on.format('YYYY-MM-DD') : null,
      });
      message.success('Notice sab staff ke dashboard par chala gaya');
      setModal(false);
      form.resetFields();
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Notice save nahi hua');
    }
    setSaving(false);
  };

  const submitFestival = async (values) => {
    setSaving(true);
    try {
      await api.post('/festivals', {
        name: values.name,
        greeting: values.greeting || values.name,
        deity: values.deity,
        message: values.message,
        festival_date: values.festival_date ? values.festival_date.format('YYYY-MM-DD') : null,
        month_day: values.month_day || null,
      });
      message.success('Festival add ho gaya');
      setFestModal(false);
      festForm.resetFields();
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Festival save nahi hua');
    }
    setSaving(false);
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/notices/${row.id}`, { is_active: row.is_active ? 0 : 1 });
      load();
    } catch {
      message.error('Update nahi hua');
    }
  };

  const showReaders = async (row) => {
    try {
      const r = await api.get(`/notices/${row.id}/readers`);
      setReaders({ notice: row, list: Array.isArray(r.data) ? r.data : [] });
    } catch {
      message.error('Readers load nahi hue');
    }
  };

  const noticeColumns = [
    {
      title: 'Notice',
      dataIndex: 'body',
      render: (body, row) => (
        <div>
          {row.title && <div style={{ fontWeight: 600 }}>{row.title}</div>}
          <div style={{ whiteSpace: 'pre-wrap' }}>{body}</div>
          {row.link && <a href={row.link} target="_blank" rel="noopener noreferrer">{row.link}</a>}
        </div>
      ),
    },
    {
      title: 'Kis ko',
      dataIndex: 'company_name',
      width: 150,
      render: (c) => c ? <Tag color="blue">{c}</Tag> : <Tag color="green">Saare staff</Tag>,
    },
    {
      title: 'Importance',
      dataIndex: 'importance',
      width: 110,
      render: (i) => <Tag color={i === 'urgent' ? 'red' : i === 'important' ? 'orange' : 'default'}>{i}</Tag>,
    },
    {
      title: 'Padha',
      dataIndex: 'read_count',
      width: 120,
      render: (c, row) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => showReaders(row)}>
          {c}{staffTotal ? ` / ${staffTotal}` : ''}
        </Button>
      ),
    },
    {
      title: 'Kab',
      dataIndex: 'created_at',
      width: 150,
      render: (d, row) => (
        <div>
          <div>{dayjs(d).format('DD MMM, hh:mm A')}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{row.created_by_name}</Text>
        </div>
      ),
    },
    {
      title: 'Action',
      width: 120,
      render: (_, row) => (
        <Popconfirm title={row.is_active ? 'Notice band kar dein?' : 'Notice dobara chalu karein?'} onConfirm={() => toggleActive(row)}>
          <Button size="small" danger={!!row.is_active}>{row.is_active ? 'Band karo' : 'Chalu karo'}</Button>
        </Popconfirm>
      ),
    },
  ];

  const festivalColumns = [
    {
      title: 'Date',
      render: (_, r) => r.festival_date
        ? dayjs(r.festival_date).format('DD MMM YYYY')
        : <Tag color="purple">Har saal {r.month_day}</Tag>,
      width: 170,
    },
    { title: 'Festival', dataIndex: 'name' },
    { title: 'Message', dataIndex: 'greeting' },
    {
      title: 'Photo',
      dataIndex: 'deity',
      width: 110,
      render: (d) => (
        <img
          src={`/festivals/${d === 'flag' ? 'flag.png' : d + '.jpg'}`}
          alt={d}
          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '50%' }}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 120,
      render: (a, row) => (
        <Button
          size="small"
          danger={!!a}
          onClick={async () => {
            await api.put(`/festivals/${row.id}`, { is_active: a ? 0 : 1 });
            load();
          }}
        >
          {a ? 'Band karo' : 'Chalu karo'}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}><NotificationOutlined /> Notice Board</Title>
      <Text type="secondary">
        Notice likhte hi sab staff ke dashboard par upar dikh jayega, aur jab tak wo "Padh liya" na dabaye tab tak hatega nahi.
      </Text>

      <Tabs
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'notices',
            label: 'Notices',
            children: (
              <Card
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setModal(true)}>Naya Notice</Button>}
              >
                <Table
                  rowKey="id"
                  dataSource={notices}
                  columns={noticeColumns}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 900 }}
                />
              </Card>
            ),
          },
          {
            key: 'festivals',
            label: <span><CalendarOutlined /> Festival Calendar</span>,
            children: (
              <Card
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setFestModal(true)}>Festival Add</Button>}
              >
                <Text type="secondary">
                  Festival ke din sabke dashboard par poore din popup aata hai (chhutti ho ya na ho). Ekadashi apne aap
                  tithi se aati hai — mahine me 2 baar. Date galat lage to yahan se sahi kar do.
                </Text>
                <Table
                  style={{ marginTop: 12 }}
                  rowKey="id"
                  dataSource={festivals}
                  columns={festivalColumns}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 800 }}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title="Naya Notice"
        open={modal}
        onCancel={() => setModal(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="Sab staff ko bhejo"
      >
        <Form form={form} layout="vertical" onFinish={submit} initialValues={{ importance: 'normal' }}>
          <Form.Item name="title" label="Title (optional)">
            <Input placeholder="Jaise: Kal office 11 baje khulega" />
          </Form.Item>
          <Form.Item name="body" label="Notice" rules={[{ required: true, message: 'Notice likho' }]}>
            <TextArea rows={5} placeholder="Jo baat sab staff ko batani hai..." />
          </Form.Item>
          <Form.Item name="link" label="Link (optional)">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="importance" label="Importance">
            <Select
              options={[
                { value: 'normal', label: 'Normal' },
                { value: 'important', label: 'Important' },
                { value: 'urgent', label: 'Zaroori (popup bhi aayega)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="company_id" label="Company (khali chhodo to saare staff ko)">
            <Select allowClear placeholder="Saare staff" options={companies.map(c => ({ value: c.id, label: c.name }))} />
          </Form.Item>
          <Form.Item name="expires_on" label="Is date ke baad hata do (optional)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Festival Add / Date Sudhaar"
        open={festModal}
        onCancel={() => setFestModal(false)}
        onOk={() => festForm.submit()}
        confirmLoading={saving}
      >
        <Form form={festForm} layout="vertical" onFinish={submitFestival} initialValues={{ deity: 'shubh' }}>
          <Form.Item name="name" label="Festival ka naam" rules={[{ required: true, message: 'Naam likho' }]}>
            <Input placeholder="Jaise: Raksha Bandhan" />
          </Form.Item>
          <Form.Item name="greeting" label="Popup par kya likha aaye">
            <Input placeholder="Happy Raksha Bandhan" />
          </Form.Item>
          <Form.Item name="deity" label="Photo (devta ke hisaab se)">
            <Select options={DEITIES} />
          </Form.Item>
          <Form.Item name="festival_date" label="Date (is saal ki)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="month_day" label="Ya har saal isi English date par (MM-DD)">
            <Input placeholder="08-15" />
          </Form.Item>
          <Form.Item name="message" label="Chhota message (optional)">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Kaun padh chuka hai — ${readers?.notice?.title || 'Notice'}`}
        open={!!readers}
        onCancel={() => setReaders(null)}
        footer={null}
      >
        <List
          dataSource={readers?.list || []}
          locale={{ emptyText: 'Abhi kisi ne nahi padha' }}
          renderItem={(u) => (
            <List.Item>
              <Space>
                <b>{u.full_name}</b>
                <Text type="secondary">{u.username}</Text>
              </Space>
              <Text type="secondary">{dayjs(u.read_at).format('DD MMM, hh:mm A')}</Text>
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
}
